import { BaseFetcher, FetcherMeta } from '@Types/fetcher'
import { useEffect, useState } from 'react'
import refresh from './refresh'
import { useRemoteAppActions, useRemoteAppState } from '@Store/slices/remoteApp'
import { type PrisyncError, UnauthorizedError } from '@Types/PrisyncError'
import { CacheState, useCacheActions } from '@Store/slices/cache'

function useBaseFetcher(hasAuth = false): BaseFetcher {
  const [meta, setMeta] = useState<FetcherMeta>({
    isInitialized: false,
    isFinished: false,
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
    data: null,
    url: '',
    options: {},
  })

  const { accessToken, refreshToken } = useRemoteAppState()
  const { setAppTokens } = useRemoteAppActions()
  const { invalidate } = useCacheActions()

  useEffect(() => {
    if (meta && meta.error) {
      throw meta.error
    }
  }, [meta])

  const fetcher = async (
    url: string,
    options: RequestInit,
    callback?: (...args: any[]) => void,
    invalidateCacheKeys?: (keyof CacheState)[],
  ) => {
    try {
      if (hasAuth && (!accessToken || !refreshToken)) {
        throw new UnauthorizedError()
      }

      if (hasAuth) {
        if (!options) options = { headers: {} }
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${accessToken}`,
        }
      }

      setMeta((prev) => ({
        ...prev,
        isInitialized: true,
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        data: null,
        url,
        options,
      }))

      let response = await fetch(url, options)

      let hasError = !response.ok
      let result
      try {
        result = await response.json()
      } catch (error) {
        result = null
      }

      if (hasAuth && response.status === 401) {
        const { result: refreshResult, hasError: hasRefreshError } =
          await refresh(refreshToken as string)

        if (hasRefreshError) {
          throw new UnauthorizedError()
        }

        setAppTokens(refreshResult)

        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${refreshResult?.access_token}`,
        }

        response = await fetch(url, options)

        hasError = !response.ok
        result = await response.json()
      }

      if (callback) {
        callback(result, hasError)
      }

      setMeta((prev) => ({
        ...prev,
        isLoading: false,
        isFinished: true,
        isSuccess: !hasError,
        isError: hasError,
        error: hasError ? result : null,
        data: hasError ? null : result,
      }))
    } catch (error: Error | PrisyncError | any) {
      setMeta((prev) => ({
        ...prev,
        isLoading: false,
        isFinished: true,
        isError: true,
        error: error,
      }))
    }

    if (invalidateCacheKeys && invalidateCacheKeys.length > 0) {
      invalidate(invalidateCacheKeys)
    }
  }

  return [fetcher, { ...meta }, setMeta]
}

export default useBaseFetcher
